import { Link, useLocation, useSearchParams } from "react-router-dom";
import { WERKSTATT_BRANCHES, type WerkstattBranch } from "../../lib/werkstattBranches";
import { DEV_CARDS } from "../../lib/devCards";

/**
 * Gemeinsamer Header für alle Werkstatt-Zweige. Zeigt das Werkstatt-H2
 * + Branch-Selektor-Tabs.
 *
 * Erweiterungs-Pattern: neuer Zweig = neuer Eintrag in WERKSTATT_BRANCHES +
 * neue Body-Komponente. Header bleibt unverändert.
 */
export function WerkstattBranchHeader(props: {
  activeBranch: WerkstattBranch;
  modal?: boolean;
}) {
  const loc = useLocation();
  const [sp] = useSearchParams();
  const currentCard = sp.get("card");
  // Dev-Karten-Quick-Access nur auf dem Abrechnungs-Zweig zeigen (andere Zweige
  // sind nicht karten-bezogen — Zugriffs-Matrix arbeitet rollen-orientiert,
  // Bestand monatsweit).
  const showDevCards = props.activeBranch === "abrechnung";
  return (
    <div className="werkstatt-header">
      {props.modal && (
        <div style={{ marginBottom: 8 }}>
          <span style={{ fontSize: 11, color: "#6b7280" }}>
            Modal-Modus — schließen kehrt zum Kontext zurück.
          </span>
        </div>
      )}
      <h2 style={{ margin: 0 }}>Pipeline-Werkstatt</h2>
      <p className="muted" style={{ margin: "4px 0 12px 0", maxWidth: 820 }}>
        Glass-Box-Doku der Produktions-Anwendung. Jeder Zweig macht einen
        eigenständigen Teil des Systems sichtbar.
      </p>

      <div
        className="werkstatt-branch-tabs"
        role="tablist"
        style={{
          display: "flex",
          gap: 0,
          marginBottom: 16,
          borderBottom: "2px solid #1d4ed8",
        }}
      >
        {WERKSTATT_BRANCHES.map((b) => {
          const active = b.slug === props.activeBranch;
          return (
            <Link
              key={b.slug}
              to={`/werkstatt/${b.slug}/${b.defaultSection}`}
              role="tab"
              aria-selected={active}
              className={"werkstatt-branch-tab " + (active ? "active" : "")}
              style={{
                padding: "8px 16px",
                background: active ? "#1d4ed8" : "transparent",
                color: active ? "white" : "#1d4ed8",
                textDecoration: "none",
                fontWeight: 600,
                fontSize: 14,
                border: active ? "2px solid #1d4ed8" : "2px solid transparent",
                borderBottom: "none",
                borderRadius: "4px 4px 0 0",
                marginRight: 4,
              }}
              title={b.subtitle}
            >
              {b.title}
            </Link>
          );
        })}
      </div>

      {showDevCards && (
        <div
          style={{
            display: "flex", alignItems: "center", gap: 8,
            marginBottom: 16, padding: "6px 10px",
            background: "#fef3c7", border: "1px solid #fbbf24",
            borderRadius: 4, fontSize: 12,
          }}
          title="Vorkonfigurierte Demo-Karten für die Glass-Box-Reviews — Constants in lib/devCards.ts"
        >
          <strong style={{ color: "#92400e" }}>Dev-Karten:</strong>
          {DEV_CARDS.map((d) => {
            const active = currentCard === d.id;
            // Aktuellen Pfad behalten, nur ?card austauschen — so wechselt der
            // User zwischen TS- und EX-Demo ohne die aktive Phase zu verlieren.
            const search = new URLSearchParams(sp);
            search.set("card", d.id);
            return (
              <Link
                key={d.id}
                to={`${loc.pathname}?${search.toString()}`}
                title={d.hint}
                style={{
                  padding: "3px 10px", borderRadius: 12,
                  background: active ? "#92400e" : "white",
                  color: active ? "white" : "#92400e",
                  border: "1px solid #92400e",
                  textDecoration: "none", fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {d.label}
              </Link>
            );
          })}
          <span style={{ marginLeft: "auto", color: "#92400e", opacity: 0.7 }}>
            wechseln behält die Phase
          </span>
        </div>
      )}
    </div>
  );
}
